<template>
    <div class="split-view-wrapper">
        <div class="scroll-column border-right">
            <div class="column-header">
                <i class="pi pi-sparkles text-orange-500"></i>
                <h2>AI Summaries</h2>
            </div>

            <div class="grid-layout">
                <div v-for="(sum, index) in Summaries" :key="'sum-' + index" class="card-wrapper">
                    <Card class="custom-card">
                        <template #content>
                            <div class="card-content-top">
                                <span class="note-title">{{ sum.summary.studentSummary.title }}</span>
                                <span class="Paragraph-Medium block mb-2">
                                    {{ formatToDisplayDate(sum.classInstance.start) }}
                                </span>

                                <div class="flex flex-wrap gap-1 mb-3">
                                    <span v-for="point in sum.summary.studentSummary.keyPoints" :key="point" class="tag">
                                        {{ point }}
                                    </span>
                                </div>

                                <div class="note-text" v-html="sum.summary.studentSummary.overview"></div>
                            </div>
                            <div class="flex flex-wrap gap-1 mb-3 mt-3">
                                <span class="tag2" v-if="role === 'tutor'">
                                    <b style="color: #1e293b;">Student :</b> {{
                                        cleanupLables(sum.classInstance.studentName) }}
                                </span>
                                <span class="tag2" v-else>
                                    <b style="color: #1e293b;">Tutor :</b> {{ cleanupLables(sum.classInstance.tutorName)
                                    }}
                                </span>
                                <span class="tag2">
                                    <b style="color: #1e293b;">Subject :</b> {{
                                        cleanupLables(sum.classInstance.classId.subject) }}
                                </span>
                                <span class="tag2">
                                    <b style="color: #1e293b;">Grade :</b> {{
                                        cleanupLables(sum.classInstance.classId.grade) }}
                                </span>
                            </div>
                        </template>
                        <template #footer>
                            <Button label="View Full Summary" class="orange-button-small w-full"
                                @click="$emit('view-summary', sum)" />
                        </template>
                    </Card>
                </div>
            </div>
        </div>

        <div class="scroll-column">
            <div class="column-header">
                <i class="pi pi-comments text-teal-500"></i>
                <h2>Class Transcripts</h2>
            </div>

            <div v-if="Transcripts && Transcripts.length > 0" class="grid-layout">
                <div v-for="(trans, index) in Transcripts" :key="'trans-' + index" class="card-wrapper">
                    <Card class="custom-card">
                        <template #content>
                            <div class="card-content-top">
                                <span class="note-title">Session Transcript</span>
                                <span class="Paragraph-Medium block mb-1">
                                    {{ formatToDisplayDate(trans.classInstance.date) }}
                                </span>
                                <span class="duration-label">
                                    {{ Math.floor(trans.transcript.durationSeconds / 60) }} mins total
                                </span>

                                <div class="note-text italic mt-3">
                                    "{{ trans.transcript.text }}"
                                </div>
                            </div>
                            <div class="flex flex-wrap gap-1 mb-3 mt-3">
                                <span class="tag2-green" v-if="role === 'tutor'">
                                    <b style="color: #1e293b;">Student :</b> {{
                                        cleanupLables(trans.classInstance.studentName) }}
                                </span>
                                <span class="tag2-green" v-else>
                                    <b style="color: #1e293b;">Tutor :</b> {{
                                        cleanupLables(trans.classInstance.tutorName) }}
                                </span>
                                <span class="tag2-green">
                                    <b style="color: #1e293b;">Subject :</b> {{
                                        cleanupLables(trans.classInstance.classId.subject) }}
                                </span>
                                <span class="tag2-green">
                                    <b style="color: #1e293b;">Grade :</b> {{
                                        cleanupLables(trans.classInstance.classId.grade) }}
                                </span>
                            </div>
                        </template>
                        <template #footer>
                            <Button label="View Transcript" class="green-button-small w-full"
                                @click="$emit('view-transcript', trans)" />
                        </template>
                    </Card>
                </div>
            </div>

            <div v-else class="empty-state-container">
                <p class="empty-message">No transcripts available yet.</p>
                <span class="feature-tag">Upcoming Feature</span>
            </div>
        </div>
    </div>
</template>

<script setup>
import Card from "primevue/card";
import Button from "primevue/button";
import { cleanupLables, formatToDisplayDate } from "@/utils/cleanups";
import { defineProps, defineEmits, computed } from "vue";
import { useAuthStore } from "@/stores/authStore";
defineProps({
    Summaries: {
        type: Array,
        required: true
    },
    Transcripts: {
        type: Array,
        required: true
    }
});

defineEmits(['view-summary', 'view-transcript']);

const authStore = useAuthStore();
const role = computed(() => authStore.user?.role || "");
</script>

<style scoped>
.split-view-wrapper {
    display: flex;
    height: calc(100vh - 100px);
    /* Adjust based on your top nav height */
    width: 100%;
    overflow: hidden;
    /* Prevents main body from scrolling */
}

.scroll-column {
    flex: 1;
    overflow-y: auto;
    padding: 1.5rem;
    height: 100%;
}

.bg-gray-transcripts {
    background-color: #f8fafc;
}

.border-right {
    border-right: 1px solid #e2e8f0;
}

.column-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 2rem;
    position: sticky;
    top: -1.5rem;
    /* Matches padding */
    background: inherit;
    padding: 1rem 0;
    z-index: 10;
}

.column-header h2 {
    font-size: 1.25rem;
    font-weight: 700;
    margin: 0;
}

.grid-layout {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

.card-wrapper {
    display: flex;
    flex-direction: column;
}

/* Make PrimeVue card fill the wrapper and push footer to bottom */
.card-wrapper :deep(.p-card) {
    height: 100%;
    display: flex;
    flex-direction: column;
}

.card-wrapper :deep(.p-card-body) {
    flex: 1;
    display: flex;
    flex-direction: column;
}

.card-wrapper :deep(.p-card-content) {
    flex: 1;
    display: flex;
    flex-direction: column;
}

.card-content-top {
    flex: 1;
}

.custom-card {
    border-radius: 8px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    border: 1px solid #f1f5f9;
    padding: 1rem;
    min-height: 280px;
}

.note-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: #1e293b;
    display: block;
    margin-bottom: 0.25rem;
}

.note-text {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    color: #64748b;
    font-size: 0.9rem;
    line-height: 1.4;
}

.tag {
    background: #fff1e6;
    color: #d97706;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 500;
}

.tag2 {
    background: #ffffff;
    color: #d97706;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 500;
}

.tag2-green {
    background: #ffffff;
    color: #0d9488;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 500;
}

.duration-label {
    font-size: 0.8rem;
    color: #0d9488;
    font-weight: 600;
}

.italic {
    font-style: italic;
}

/* Scrollbar Styling for a cleaner look */
.scroll-column::-webkit-scrollbar {
    width: 6px;
}

.scroll-column::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 10px;
}

.empty-state-container {
    padding: 2rem;
    text-align: center;
    border: 1px dashed #ccc;
    border-radius: 8px;
    margin-top: 1rem;
    background-color: #f9f9f9;
}

.empty-message {
    color: #666;
    margin-bottom: 0.5rem;
}

.feature-tag {
    font-size: 0.75rem;
    background-color: #e0f2f1;
    color: #00796b;
    padding: 2px 8px;
    border-radius: 12px;
    text-transform: uppercase;
    font-weight: bold;
}

@media (max-width: 768px) {
    .split-view-wrapper {
        flex-direction: column;
        height: auto;
        overflow: visible;
    }

    .scroll-column {
        height: auto;
        overflow-y: visible;
        padding: 1rem;
    }

    .border-right {
        border-right: none;
        border-bottom: 1px solid #e2e8f0;
        padding-bottom: 1.5rem;
        margin-bottom: 0.5rem;
    }

    .column-header {
        justify-content: center;
        top: 0;
        padding: 0.3rem;
        background: #ffffff75;
    }
}
</style>